import * as React from "react";
import { useMotionValueEvent, useReducedMotion, useScroll } from "motion/react";
import {
  buildWorldNodes,
  distanceForProgress,
  lastNodeDistance,
  progressForNode,
  WORLD,
} from "./world";
import { resolveInitialStepIndex } from "./normalize";
import type { RoadmapData, RoadmapStep } from "./types";
import type { DriveState } from "./scene/drive";

/** One viewport of scrolling per checkpoint — the pace the journey is read at. */
const SCREENS_PER_CHECKPOINT = 1;

export function useRoadmapScroll({
  roadmap,
  scrollTrackRef,
  initialStepIndex,
  onStepChange,
  onProgressChange,
}: {
  roadmap: RoadmapData;
  scrollTrackRef: React.RefObject<HTMLElement | null>;
  initialStepIndex?: number;
  onStepChange?: (step: RoadmapStep, index: number) => void;
  onProgressChange?: (progress: number) => void;
}) {
  const reduced = useReducedMotion() ?? false;
  const steps = roadmap.steps;
  const nodes = React.useMemo(() => buildWorldNodes(steps), [steps]);

  const resumeStepIndex = React.useMemo(
    () => initialStepIndex ?? resolveInitialStepIndex(steps),
    [initialStepIndex, steps],
  );
  const resumeNodeIdx = Math.max(
    nodes.findIndex((n) => n.index === resumeStepIndex),
    0,
  );
  const initialProgress = progressForNode(nodes[resumeNodeIdx], nodes);

  const scrollHeightVh = Math.max(
    Math.round((nodes.length + 1) * 100 * SCREENS_PER_CHECKPOINT),
    220,
  );

  const driveRef = React.useRef<DriveState>({
    target: distanceForProgress(initialProgress, nodes),
    distance: distanceForProgress(initialProgress, nodes),
  });

  const progressRef = React.useRef(initialProgress);

  const { scrollYProgress } = useScroll({
    target: scrollTrackRef,
    offset: ["start start", "end end"],
  });

  // Scroll writes straight into the ref: React never re-renders while driving.
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    driveRef.current.target = distanceForProgress(v, nodes);
    progressRef.current = v;
  });

  const [focusNodeIdx, setFocusNodeIdx] = React.useState(resumeNodeIdx);
  const didResume = React.useRef(false);

  /** Scroll the window so `progress` is showing, without animating on mount. */
  const scrollToProgress = React.useCallback(
    (progress: number, behavior: ScrollBehavior) => {
      const el = scrollTrackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pageTop = (window.scrollY || document.documentElement.scrollTop) + rect.top;
      const range = Math.max(el.offsetHeight - window.innerHeight, 1);
      window.scrollTo({ top: pageTop + progress * range, behavior });
    },
    [scrollTrackRef],
  );

  React.useLayoutEffect(() => {
    if (didResume.current) return;
    didResume.current = true;
    scrollToProgress(initialProgress, "auto");
    // Layout settles a frame later when images above the scene finish sizing.
    requestAnimationFrame(() => scrollToProgress(initialProgress, "auto"));
  }, [initialProgress, scrollToProgress]);

  const focusStepIndex = nodes[focusNodeIdx]?.index ?? resumeStepIndex;
  const lastReported = React.useRef(-1);

  React.useEffect(() => {
    if (focusStepIndex === lastReported.current) return;
    lastReported.current = focusStepIndex;
    const step = steps[focusStepIndex];
    if (step) onStepChange?.(step, focusStepIndex);
    onProgressChange?.(progressRef.current);
  }, [focusStepIndex, steps, onStepChange, onProgressChange]);

  const jumpToStepIndex = React.useCallback(
    (stepIndex: number) => {
      const node = nodes.find((n) => n.index === stepIndex);
      if (!node) return;
      scrollToProgress(progressForNode(node, nodes), reduced ? "auto" : "smooth");
    },
    [nodes, scrollToProgress, reduced],
  );

  return {
    nodes,
    driveRef,
    scrollYProgress,
    scrollHeightVh,
    focusNodeIdx,
    focusStepIndex,
    setFocusNodeIdx,
    resumeStepIndex,
    jumpToStepIndex,
    reduced,
    roadLength: lastNodeDistance(nodes) + WORLD.runOut,
  };
}
